import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { PANEL_LOGIN_PATH } from '@/shared/config/flavor';

/**
 * Gate de acesso. Comportamento:
 *  1. Nao logado → redireciona pra home
 *  2. Logado mas gmInfo nao chegou ainda → placeholder
 *  3. Logado e gmLevel < requiredLevel → tela "Acesso restrito"
 *  4. OK → renderiza children
 *
 * Se um `feature` for passado, o gate confere `gmInfo.features.includes(feature)`
 * (mais granular do que level). Caso contrario, exige level >= requiredLevel (default=1).
 */
export function GmGate({
  children, requiredLevel = 1, feature,
}: {
  children: ReactNode;
  requiredLevel?: number;
  feature?: string;
}) {
  const { isLoggedIn, gmInfo, hasFeature } = useAuth();

  if (!isLoggedIn) return <Navigate to={PANEL_LOGIN_PATH} replace />;

  if (gmInfo === null) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="surface-card rounded-xl p-6 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-[#00e1d7] animate-pulse" />
          <span className="text-sm text-white/70">Verificando permissoes...</span>
        </div>
      </div>
    );
  }

  const allowed = feature
    ? hasFeature(feature)
    : gmInfo.gmLevel >= requiredLevel;

  if (!allowed) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="surface-card rounded-xl p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/30
                          mx-auto mb-4 flex items-center justify-center">
            <ShieldAlert size={26} className="text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-white mb-2">Acesso restrito</h2>
          <p className="text-sm text-white/65 mb-2">
            Essa área exige nível {requiredLevel}+ de GM ou permissão específica.
          </p>
          <p className="text-xs text-white/45">
            Seu nível atual: {gmInfo.gmLevel}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
